import { useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { generateInvoicePDF } from "@/services/invoice-pdf.service.ts";
import type { InvoiceRecord } from "@/repositories/invoice-records";

export function InvoiceBillTo({ invoice }: { invoice: InvoiceRecord }) {
  return (
    <section aria-label="Bill to">
      <p className="text-sm text-muted-foreground uppercase">Bill To:</p>
      <p className="font-medium mt-4">{invoice.bill_to.name}</p>
      {invoice.bill_to.address && <p className="text-sm whitespace-pre-line">{invoice.bill_to.address}</p>}
      {invoice.billing_email && <p className="text-sm text-muted-foreground">{invoice.billing_email}</p>}
    </section>
  );
}

export function InvoiceLineItemsTable({ invoice }: { invoice: InvoiceRecord }) {
  if (invoice.line_items.length === 0) return <div className="text-center py-8 text-muted-foreground">No line items found</div>;

  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="border-b">
          <th className="text-left py-2 font-semibold">Particular</th>
          <th className="text-left py-2 font-semibold">Description</th>
          <th className="text-right py-2 font-semibold">Unit Price</th>
          <th className="text-center py-2 font-semibold">Qty</th>
          <th className="text-right py-2 font-semibold">Total Price</th>
        </tr>
      </thead>
      <tbody>
        {invoice.line_items.map((item) => (
          <tr key={item.id} className="border-b align-top">
            <td className="py-2">{item.particular}</td>
            <td className="py-2 text-muted-foreground">{item.description}</td>
            <td className="py-2 text-right">${item.unit_price.toFixed(2)}</td>
            <td className="py-2 text-center">{item.quantity}</td>
            <td className="py-2 text-right">${item.total_price.toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={4} className="py-2 font-semibold text-right border-t">
            Total
          </td>
          <td className="py-2 font-semibold text-right border-t">${invoice.amount.toFixed(2)}</td>
        </tr>
      </tfoot>
    </table>
  );
}

export function InvoicePdfButton({ invoice, label }: { invoice: InvoiceRecord; label: string }) {
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      await generateInvoicePDF(invoice, invoice.line_items);
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Button variant="outline" size="sm" onClick={download} disabled={downloading}>
      <Download className="w-4 h-4 mr-2" /> {downloading ? "Generating..." : label}
    </Button>
  );
}
